import { h, Fragment } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import { useSize } from 'utils/dom'

import useSSR from '@core/hooks/useSSR'
import { modelTypes } from 'app/constants'
import translate from 'core/hooks/useResource'
import CompanyInfo from './CompanyInfo'
import Newsletter from './FooterNewsletter'

function addColumnOneLinks(columnItems, links) {
	const col = [...columnItems]

	links.blog &&
		col.push({
			name: translate('blog'),
			url: links.blog,
		})

	links.contact &&
		col.push({
			name: translate('contactus'),
			url: links.contact,
		})

	links.forums &&
		col.push({
			name: translate('forum.forums'),
			url: links.forums,
		})

	links.sitemap &&
		col.push({
			name: translate('sitemap'),
			url: links.sitemap,
		})

	return col
}

function addColumnTwoLinks(columnItems, links) {
	const col = [...columnItems]

	links.search &&
		col.push({
			name: translate('search'),
			url: links.search,
		})

	links.recentlyViewedProducts &&
		col.push({
			name: translate('products.recentlyViewedProducts'),
			url: links.recentlyViewedProducts,
		})

	links.newProducts &&
		col.push({
			name: translate('products.newProducts'),
			url: links.newProducts,
		})

	links.compareProducts &&
		col.push({
			name: translate('products.compare.list'),
			url: links.compareProducts,
		})

	links.news &&
		col.push({
			name: translate('news'),
			url: links.news,
		})

	return col
}

function addColumnThreeLinks(columnItems, links) {
	const col = [...columnItems]

	links.customerInfo &&
		col.push({
			name: translate('account.myaccount'),
			url: links.customerInfo,
		})

	links.customerOrders &&
		col.push({
			name: translate('account.customerorders'),
			url: links.customerOrders,
		})

	links.customerAddresses &&
		col.push({
			name: translate('account.customeraddresses'),
			url: links.customerAddresses,
		})

	links.giftcards &&
		col.push({
			name: translate('CheckGiftCardBalance'),
			url: links.giftcards,
		})

	links.cart &&
		col.push({
			name: translate('ShoppingCart'),
			url: links.cart,
		})

	links.wishlist &&
		col.push({
			name: translate('wishlist'),
			url: links.wishlist,
		})

	links.vendor &&
		col.push({
			name: translate('vendors.applyaccount'),
			url: links.vendor,
		})

	return col
}

function Column({ number, title, columnTopics }) {
	const [open, setOpen] = useState(false)
	const { isDesktop } = useSize()

	return (
		<div className={`footer-block col-${number}`}>
			{!isDesktop ? (
				<Fragment>
					<div
						className="flex align-center justify-between w-100"
						onClick={() => setOpen(!open)}
					>
						<span className="title">{title}</span>

						<svg
							style={{ transform: open ? 'none' : 'rotate(45deg)' }}
							width="20"
							height="20"
							viewBox="0 0 28 28"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path d="M3.14905 3.3874L3.26256 3.26256C3.90578 2.61935 4.92513 2.58151 5.6126 3.14905L5.73744 3.26256L14 11.525L22.2626 3.26256C22.946 2.57915 24.054 2.57915 24.7374 3.26256C25.4209 3.94598 25.4209 5.05402 24.7374 5.73744L16.475 14L24.7374 22.2626C25.3807 22.9058 25.4185 23.9251 24.8509 24.6126L24.7374 24.7374C24.0942 25.3807 23.0749 25.4185 22.3874 24.8509L22.2626 24.7374L14 16.475L5.73744 24.7374C5.05402 25.4209 3.94598 25.4209 3.26256 24.7374C2.57915 24.054 2.57915 22.946 3.26256 22.2626L11.525 14L3.26256 5.73744C2.61935 5.09422 2.58151 4.07487 3.14905 3.3874L3.26256 3.26256L3.14905 3.3874Z"></path>
						</svg>
					</div>

					{open && (
						<Fragment>
							{columnTopics && columnTopics.length > 0 && (
								<ul>
									{columnTopics.map(topic => (
										<li>
											<a href={topic.url}>{topic.name}</a>
										</li>
									))}
								</ul>
							)}
						</Fragment>
					)}
				</Fragment>
			) : (
				<Fragment>
					{title && <div className="title">{title}</div>}

					{columnTopics && columnTopics.length > 0 && (
						<ul>
							{columnTopics.map(topic => (
								<li>
									<a href={topic.url}>{topic.name}</a>
								</li>
							))}
						</ul>
					)}
				</Fragment>
			)}
		</div>
	)
}

export default function FooterColumns({
	topics,
	links,
	storeName,
	email,
	phone,
	address,
	socialSettings,
}) {
	const {
		mainStoreUrl,
	} = useSSR([modelTypes.mainStoreUrl])

	let col1 = topics.filter(t => t.includeInColumn1)
	let col2 = topics.filter(t => t.includeInColumn2)
	let col3 = topics.filter(t => t.includeInColumn3)

	col1 = addColumnOneLinks(col1, links)
	col2 = addColumnTwoLinks(col2, links)
	col3 = addColumnThreeLinks(col3, links)

	col1.forEach(e => e.url = new URL(e.url, mainStoreUrl).toString())
	col2.forEach(e => e.url = new URL(e.url, mainStoreUrl).toString())
	col3.forEach(e => e.url = new URL(e.url, mainStoreUrl).toString())

	return (
		<section className="columns">
			<div className="columns__left">
				<CompanyInfo
					storeName={storeName}
					about={translate('footer.about')}
					phone={phone}
					email={email}
					address={address}
					socialSettings={socialSettings}
				/>
				<Newsletter />
			</div>

			{col1 && col1.length > 0 && (
				<Column number="1" title={translate('footer.title.col1')} columnTopics={col1} />
			)}

			{col2 && col2.length > 0 && (
				<Column number="2" title={translate('footer.title.col2')} columnTopics={col2} />
			)}

			{col3 && col3.length > 0 && (
				<Column number="3" title={translate('footer.title.col3')} columnTopics={col3} />
			)}
		</section>
	)
}
